/*
 * This approach may produce a problematic memory leak if we are constantly throwing away
 * objects. We may want to investigate this kind of approach:
 * https://addyosmani.com/backbone-fundamentals/
 *
 * @mixin
 */
function createPubSub() {
  const callbacks = {}

  function containsOneInstance(eventName, callback) {
    return callbacks[eventName] &&
      callbacks[eventName].filter(({ callback: cb }) => cb === callback).length === 1
  }

  function includesCallback(eventName, callback) {
    return callbacks[eventName] &&
      callbacks[eventName].some(({ callback: cb }) => cb === callback)
  }

  const pubSub = {
    /*
     * Returns the number of callbacks currently subscribed to a given event.
     */
    subscriberCount(eventName) {
      if (!callbacks[eventName]) {
        return 0
      }

      return callbacks[eventName].length
    },

    /*
     * @param {string} eventName Name of the event to publish
     * @param {any} event A value to be passed when the event is fired. Typically
     *    this should be a single object with the event information attached.
     */
    publish(eventName, event) {
      if (!callbacks[eventName]) {
        return
      }

      // Copying the array here is very important because if any of these handlers
      // unsubscribe it will manipulate the callback[eventName] array. We need to
      // make sure that the complete list of callbacks is executed though so we make a copy.
      [...callbacks[eventName]].forEach(({ callback, options }) => {
        if (options.once) {
          this.unsubscribe(eventName, callback)
        }
        callback(event)
      })
    },


    /*
     * @param {string} eventName Name of the event to subscribe to
     * @param {function} callback The function to be called on `eventName` events
     * @param {object} options
     */
    subscribe(eventName, callback, options = { once: false }) {
      if (!callbacks[eventName]) {
        callbacks[eventName] = []
      }

      if (includesCallback(eventName, callback)) {
        console.warn(`Already attached callback to event: ${eventName}`)

        return
      }

      callbacks[eventName].push({ callback, options: Object.assign({}, options) })
    },


    /*
     * Subscribe to an event once. The callback will be automatically unsubscribed after the
     * first event is received.
     *
     * @param {string} eventName Name of the event to subscribe to
     * @param {function} callback The function to be called on `eventName` events
     */
    once(eventName, callback) {
      pubSub.subscribe(eventName, callback, { once: true })
    },


    /*
     * @param {string} eventName Name of the event to unsubscribe from
     * @param {function} callback The callback to remove from the `eventName` subscribers list
     */
    unsubscribe(eventName, callback) {
      if (!includesCallback(eventName, callback)) {
        return
      }

      console.assert(
        containsOneInstance(eventName, callback),
        'Callback should not be included more than once',
      )
      callbacks[eventName] = callbacks[eventName].filter(({ callback: cb }) => cb !== callback)
    },
  }

  return pubSub
}

export default createPubSub
